// React dependencies
import React, { useState, useEffect, useMemo, useCallback } from 'react';

// External dependencies
import { Box, FormControl, IconButton, InputLabel, MenuItem, Select, TextField } from '@material-ui/core';
import { toast } from 'react-toastify';
import MaterialTable from 'material-table';
import { Cancel, CheckCircle, Refresh } from '@material-ui/icons';
import moment from 'moment';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';

// Internal dependencies
import Colours from '../../styles/colours';
import MText from '../common/MText';
import { Status } from '../../common/constants';
import apiService from '../../services.js/apiService';
import { IsArray, IsEmpty } from '../../common/checks';
import DateUtil from '../../common/dateUtil';
import CommentDetailModal from './component/CommentDetailModal';
import OverLoading from '../global/OverLoading';
import MButton from '../common/MButton';
import useDebounce from '../global/use-debounce';
import ProgressiveImage from '../common/ProgressiveImage';
import convert from '../../common/convert';
import ToolTip from '../common/ToolTip';
import {
  VULGARITY_CHECK_COOLDOWN_MINUTES,
  isVulgarityCheckOnCooldown,
} from './utils';
import PhotoDetailsView from '../photoDetailsView';
import { updateModalContent, toggleModal } from '../../actions/modal';

const FILTER_DEBOUNCE_MILLISECONDS = 500;

const defaultFilters = {
  id:        '',
  poiId:     '',
  shopperId: '',
  status:    Status.Pending,
};

const CommentMgmt = (props) => {
  const [comments, setComments] = useState([]);
  const [filters, setFilters] = useState(defaultFilters);
  const debouncedFilters = useDebounce(filters, FILTER_DEBOUNCE_MILLISECONDS);
  const [isFetchingComments, setIsFetchingComments] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [pagination, setPagination] = useState({
    pageNo:   0,
    pageSize: 10,
  });
  const [selectedComment, setSelectedComment] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [activePhotoId, setActivePhotoId] = useState(null);

  const handleViewComment = (commentId) => {
    setSelectedComment(commentId);
  };

  const fetchComments = async () => {
    try {
      setIsFetchingComments(true);

      const params = {
        ...filters,
        status:   filters.status === 'All' ? '' : filters.status,
        pageNo:   pagination.pageNo,
        pageSize: pagination.pageSize,
      };

      const result = await apiService.getPoiComments(params);
      const commentsList = IsArray(result.rows) ? result.rows : [];
      setComments(commentsList);
      setTotalCount(result.totalCount || 0);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setIsFetchingComments(false);
    }
  };

  const handleProcessComment = async (commentId, status) => {
    try {
      setIsUpdating(true);
      await apiService.updatePoiCommentStatus(commentId, status);
      toast.success(`Comment ${status.toLowerCase()} successfully`);
      setSelectedComment(null);
      fetchComments();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setIsUpdating(false);
    }
  };

  const triggerVulgarityCheck = async (commentId) => {
    try {
      setIsUpdating(true);

      const updatedComment = await apiService.triggerVulgarityCheck(commentId);
      const lastVulgarCheckTriggerAt = !IsEmpty(updatedComment) && !IsEmpty(updatedComment.lastVulgarCheckTriggerAt)
        ? updatedComment.lastVulgarCheckTriggerAt
        : moment().toISOString();

      setComments((currentComments) => currentComments.map((comment) => (
        comment.id === commentId
          ? { ...comment, lastVulgarCheckTriggerAt }
          : comment
      )));

      toast.success(`Vulgarity check is running. Please wait for ${VULGARITY_CHECK_COOLDOWN_MINUTES} min before triggering the action again`);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleFilterChange = useCallback((key, value) => {
    setPagination((prev) => ({ ...prev, pageNo: 0 }));
    setFilters((prev) => ({ ...prev, [key]: value }));
  }, []);

  const handlePageChange = (page) => {
    setPagination((prev) => ({ ...prev, pageNo: page }));
  };

  const handleRowsPerPageChange = (pageSize) => {
    setPagination({ pageNo: 0, pageSize });
  };

  const handleViewPhoto = (photoId) => {
    setActivePhotoId(photoId);
    setIsDialogOpen(true);
  };

  const handleOpenConfirmModal = (commentId, action) => {
    const isApprove = action === Status.Approved;

    props.updateModalContent({
      title:        `${isApprove ? 'Approve' : 'Reject'} Comment`,
      content:      `Are you sure you want to ${isApprove ? 'approve' : 'reject'} this comment?`,
      actionButton: {
        actionText: isApprove ? 'Approve' : 'Reject',
        actionType: isApprove ? 'success' : 'danger',
        action:     () => handleProcessComment(commentId, action),
      },
      cancelText:       'No',
      closeAfterAction: true,
      zIndex:           1400,
    });

    props.toggleModal();
  };

  const handleResetFilter = useCallback(() => {
    setPagination((prev) => ({ ...prev, pageNo: 0 }));
    setFilters(defaultFilters);
  }, []);

  const tableComponents = useMemo(() => ({
    Toolbar: () => {
      const [toolbarFilters, setToolbarFilters] = useState(defaultFilters);

      const onFilterChange = (key, value) => {
        setToolbarFilters((prev) => ({ ...prev, [key]: value }));
        handleFilterChange(key, value);
      };

      const onResetFilter = () => {
        setToolbarFilters(defaultFilters);
        handleResetFilter();
      };

      return (
        <div>
          <Box p={1} width="15%">
            <FormControl fullWidth>
              <MButton
                variant="outlined"
                style={{ borderColor: Colours.Primary }}
                textColour={Colours.Primary}
                size="medium"
                text="Clear Filters"
                onClick={onResetFilter}
              />
            </FormControl>
          </Box>
          <Box display="flex" justifyContent="flex-start" alignItems="end" m={0} p={1} bgcolor="background.paper">
            <Box p={1} width="17%">
              <FormControl fullWidth>
                <TextField
                  label="Comment Id"
                  type="number"
                  value={toolbarFilters.id || ''}
                  onChange={(e) => onFilterChange('id', e.target.value)}
                  fullWidth
                />
              </FormControl>
            </Box>
            <Box p={1} width="17%">
              <FormControl fullWidth>
                <TextField
                  label="POI Id"
                  type="number"
                  value={toolbarFilters.poiId || ''}
                  onChange={(e) => onFilterChange('poiId', e.target.value)}
                  fullWidth
                />
              </FormControl>
            </Box>
            <Box p={1} width="17%">
              <FormControl fullWidth>
                <TextField
                  label="Shopper Id"
                  type="number"
                  value={toolbarFilters.shopperId || ''}
                  onChange={(e) => onFilterChange('shopperId', e.target.value)}
                  fullWidth
                />
              </FormControl>
            </Box>

            <Box p={1} width="17%">
              <FormControl fullWidth>
                <InputLabel id="status-filter-label">Status</InputLabel>
                <Select
                  labelId="status-filter-label"
                  value={toolbarFilters.status}
                  onChange={(e) => onFilterChange('status', e.target.value)}
                >
                  <MenuItem value="All">All</MenuItem>
                  <MenuItem value={Status.Pending}>{Status.Pending}</MenuItem>
                  <MenuItem value={Status.Approved}>{Status.Approved}</MenuItem>
                  <MenuItem value={Status.Rejected}>{Status.Rejected}</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </Box>
        </div>
      );
    },
  }), [handleFilterChange, handleResetFilter]);

  const columns = [
    {
      title:  'Id',
      render: (rowData) => (
        <MButton
          text={rowData.id}
          variant="text"
          textColour={Colours.Blue}
          onClick={() => handleViewComment(rowData.id)}
        />
      ),
      filtering: false,
      cellStyle: {
        width:     '1%',
        textAlign: 'center',
      },
      headerStyle: {
        textAlign: 'center',
      },
    },
    {
      title:     'Photo',
      filtering: false,
      cellStyle: {
        width:     '10%',
        textAlign: 'center',
      },
      headerStyle: {
        textAlign: 'center',
      },
      render: (rowData) => {
        if (IsEmpty(rowData.photo)) {
          return null;
        }

        const { photoUrl } = rowData.photo;

        return (
          <Box
            style={{ cursor: 'pointer', position: 'relative' }}
            onClick={() => handleViewPhoto(rowData.photo.id)}
          >
            <ProgressiveImage
              image={convert.imageResizeUrl(photoUrl, 150, 150)}
              height={150}
              width={150}
              style={{
                objectFit:    'cover',
                borderRadius: 4,
              }}
            />
          </Box>
        );
      },
    },
    {
      title:     'Comment',
      filtering: false,
      cellStyle: {
        width: '15%',
      },
      render: (rowData) => (
        <MText
          text={rowData.newText}
          style={{
            display:         '-webkit-box',
            overflow:        'hidden',
            textOverflow:    'ellipsis',
            WebkitBoxOrient: 'vertical',
            WebkitLineClamp: 6,
          }}
        />
      ),
    },
    {
      title:     'Shopper Id',
      field:     'shopperId',
      filtering: false,
      cellStyle: {
        width:     '2%',
        textAlign: 'center',
      },
      headerStyle: {
        textAlign: 'center',
      },
    },
    {
      title:  'Poi Id',
      render: (rowData) => (
        <MButton
          text={rowData.poiId}
          variant="text"
          textColour={Colours.Blue}
          onClick={() => window.open(`/admin/poi/${rowData.poiId}`, '_blank')}
        />
      ),
      filtering: false,
      cellStyle: {
        width:     '2%',
        textAlign: 'center',
      },
      headerStyle: {
        textAlign: 'center',
      },
    },
    {
      title:     'Status',
      field:     'status',
      filtering: false,
      cellStyle: {
        width:     '1%',
        textAlign: 'center',
      },
      headerStyle: {
        textAlign: 'center',
      },
    },
    {
      title:     'Created At',
      filtering: false,
      cellStyle: {
        width:     '15%',
        textAlign: 'center',
      },
      headerStyle: {
        textAlign: 'center',
      },
      render: (rowData) => (
        <MText text={DateUtil.CreateSGUTCDateMonthsFrom(rowData.createdAt, 0, 'D MMM YYYY HH:mm:ss')} />
      ),
    },
    {
      title:     'Last Edited At',
      filtering: false,
      cellStyle: {
        width:     '15%',
        textAlign: 'center',
      },
      headerStyle: {
        textAlign: 'center',
      },
      render: (rowData) => (
        <MText text={!IsEmpty(rowData.editPendingDate)
          ? DateUtil.CreateSGUTCDateMonthsFrom(rowData.editPendingDate, 0, 'D MMM YYYY HH:mm:ss')
          : '-'}
        />
      ),
    },
    {
      title:     'Vulgar Check Trigger',
      filtering: false,
      cellStyle: {
        width:     '15%',
        textAlign: 'center',
      },
      headerStyle: {
        textAlign: 'center',
      },
      render: (rowData) => {
        const onCooldown = isVulgarityCheckOnCooldown(rowData.lastVulgarCheckTriggerAt);

        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
            <MText
              text={!IsEmpty(rowData.lastVulgarCheckTriggerAt)
                ? DateUtil.CreateSGUTCDateMonthsFrom(rowData.lastVulgarCheckTriggerAt, 0, 'D MMM YYYY HH:mm:ss')
                : '-'}
            />
            {
              rowData.status === Status.Pending && (
                <ToolTip
                  title={onCooldown
                    ? `Vulgarity Check can be triggered after ${VULGARITY_CHECK_COOLDOWN_MINUTES} minutes from the last check`
                    : 'Trigger Vulgarity Check'}
                >
                  <IconButton
                    size="medium"
                    title="Trigger vulgarity checker"
                    disabled={isFetchingComments || isUpdating || onCooldown}
                    onClick={() => triggerVulgarityCheck(rowData.id)}
                    style={{
                      padding: '8px',
                      color:   onCooldown ? Colours.Gray1 : Colours.Yellow,
                    }}
                  >
                    <Refresh fontSize="medium" />
                  </IconButton>
                </ToolTip>
              )
            }
          </Box>
        );
      },
    },
    {
      title:     'Actions',
      filtering: false,
      cellStyle: {
        width:     '15%',
        textAlign: 'center',
      },
      headerStyle: {
        textAlign: 'center',
      },
      render: (rowData) => (
        <Box style={{ display: 'flex', justifyContent: 'center', gap: 20 }}>
          {
            [Status.Pending, Status.Rejected].includes(rowData.status) && (
              <ToolTip title="Approve">
                <IconButton
                  size="medium"
                  style={{
                    color:   Colours.Green1,
                    padding: '8px',
                  }}
                  onClick={() => handleOpenConfirmModal(rowData.id, Status.Approved)}
                  title="Approve"
                  disabled={isFetchingComments || isUpdating}
                >
                  <CheckCircle fontSize="medium" />
                </IconButton>
              </ToolTip>
            )
          }
          {[Status.Pending, Status.Approved].includes(rowData.status) && (
            <ToolTip title="Reject">
              <IconButton
                size="medium"
                style={{
                  color:   Colours.Red,
                  padding: '8px',
                }}
                onClick={() => handleOpenConfirmModal(rowData.id, Status.Rejected)}
                title="Reject"
                disabled={isFetchingComments || isUpdating}
              >
                <Cancel fontSize="medium" />
              </IconButton>
            </ToolTip>
          )}
        </Box>
      ),
    },
  ];

  useEffect(() => {
    fetchComments();
  }, [debouncedFilters, pagination.pageNo, pagination.pageSize]);

  return (
    <div>
      {isUpdating && <OverLoading />}
      <div className="content-header">
        <MText variant="h1" colour={Colours.Gray6} bold text="Comments Management" />
      </div>

      <MaterialTable
        columns={columns}
        data={comments}
        isLoading={isFetchingComments}
        page={pagination.pageNo}
        totalCount={totalCount}
        options={{
          pageSize:            pagination.pageSize,
          pageSizeOptions:     [10, 25, 50],
          actionsColumnIndex:  -1,
          emptyRowsWhenPaging: false,
          search:              false,
          sorting:             false,
          showTitle:           false,
          tableLayout:         'auto',
          headerStyle:         {
            backgroundColor: Colours.White,
            fontWeight:      'bold',
          },
        }}
        localization={{
          body: {
            emptyDataSourceMessage: (
              <Box sx={{ py: 4, textAlign: 'center' }}>
                <MText variant="body1" colour="textSecondary" text="No comments found" />
              </Box>
            ),
          },
        }}
        components={tableComponents}
        onChangePage={(page) => handlePageChange(page)}
        onChangeRowsPerPage={(pageSize) => handleRowsPerPageChange(pageSize)}
      />

      <CommentDetailModal
        commentId={selectedComment}
        onClose={() => setSelectedComment(null)}
        onProcessComment={handleOpenConfirmModal}
        onViewPhoto={handleViewPhoto}
      />

      <PhotoDetailsView
        open={isDialogOpen}
        photoId={!IsEmpty(activePhotoId) ? activePhotoId : ''}
        onClose={() => {
          setIsDialogOpen(false);
          setActivePhotoId(null);
        }}
      />
    </div>
  );
};

CommentMgmt.propTypes = {
  updateModalContent: PropTypes.func.isRequired,
  toggleModal:        PropTypes.func.isRequired,
};

const mapDispatchToProps = (dispatch) => ({
  toggleModal:        () => dispatch(toggleModal()),
  updateModalContent: (data) => dispatch(updateModalContent(data)),
});

export default connect(null, mapDispatchToProps)(CommentMgmt);
